/**
 * Whether the one-time terms dialog asks this person now (SOC 2 Phase 1, decision 4, 2026-10-13).
 *
 * Miles's ruling: a calm prompt AFTER the app opens — never a slower first paint, never over an answer that is still
 * arriving or a live call — that blocks use until accepted, sign-out being the only other way out, and is remembered
 * on the device so it is checked once. Pure and Firebase-free (the chat-isolation rule), so every branch is a row in
 * tests/legal/termsUpdateDecision.test.ts; TermsUpdateHost feeds it and acts on it.
 */
import { isBannerSuppressedPath } from '../../components/common/bannerSurfacePolicy';

/** What the person's own users/{uid}/terms_consents says about the CURRENT documents. */
export type TermsRecordState = 'unknown' | 'present' | 'absent' | 'unreadable';

export interface TermsUpdateInput {
  /** The first paint has happened and the browser has had an idle moment. */
  appReady: boolean;
  /** A real (not anonymous) account is signed in. */
  signedIn: boolean;
  /** REACT_APP_TERMS_UPDATE_PROMPT_ACCOUNTS names this account, or `*`. */
  leverOn: boolean;
  pathname: string;
  /** This browser knows the server kept this account's agreement to the current documents. */
  cached: boolean;
  /** A record is waiting in this browser to be sent (the person already agreed here). */
  pending: boolean;
  record: TermsRecordState;
  /** An answer is arriving, in any tab. */
  streaming: boolean;
  /** A file is uploading in this tab. */
  uploading: boolean;
  /** The workspace switch overlay is up (the dialog's layer would paint over it). */
  switchingWorkspace: boolean;
}

export type TermsUpdateDecision =
  | { action: 'none'; reason: 'app-starting' | 'signed-out' | 'lever-off' | 'exempt-route' | 'known-accepted' | 'pending-send' | 'record-present' | 'record-unreadable' }
  | { action: 'read' }
  | { action: 'hold'; reason: 'live-session-page' | 'switching-workspace' | 'streaming' | 'uploading' }
  | { action: 'ask' };

/**
 * Where the dialog never asks on its own: the sign-in and sign-up flows (bannerSurfacePolicy's mid-auth screens), the
 * connect popup, the demo, accepting an invite, and the policies themselves — the dialog's links open them in a new
 * tab, and that tab must show the text, not the dialog over it.
 */
export const TERMS_DIALOG_EXEMPT_PREFIXES = [
  '/integrations/callback',
  '/moonshot',
  '/accept-invite',
  '/terms-of-service',
  '/privacy-policy',
  '/cookie-policy',
  '/terms',
  '/privacy',
  '/legal',
] as const;

/** Pages with a live voice session or a remote desktop, which no streaming signal covers: it asks on the next page. */
export const TERMS_DIALOG_LIVE_SESSION_PREFIXES = ['/desktop', '/agent-activity'] as const;

function underPrefix(pathname: string, prefixes: readonly string[]): boolean {
  const p = (pathname || '').toLowerCase();
  return prefixes.some((s) => p === s || p.startsWith(`${s}/`));
}

export function isTermsDialogExemptPath(pathname: string): boolean {
  return isBannerSuppressedPath(pathname) || underPrefix(pathname, TERMS_DIALOG_EXEMPT_PREFIXES);
}

export function decideTermsUpdate(input: TermsUpdateInput): TermsUpdateDecision {
  if (!input.appReady) return { action: 'none', reason: 'app-starting' };
  if (!input.signedIn) return { action: 'none', reason: 'signed-out' };
  if (!input.leverOn) return { action: 'none', reason: 'lever-off' };
  if (isTermsDialogExemptPath(input.pathname)) return { action: 'none', reason: 'exempt-route' };
  if (input.cached) return { action: 'none', reason: 'known-accepted' };
  if (input.pending) return { action: 'none', reason: 'pending-send' };
  if (input.record === 'unknown') return { action: 'read' };
  if (input.record === 'present') return { action: 'none', reason: 'record-present' };
  if (input.record === 'unreadable') return { action: 'none', reason: 'record-unreadable' };
  if (underPrefix(input.pathname, TERMS_DIALOG_LIVE_SESSION_PREFIXES)) return { action: 'hold', reason: 'live-session-page' };
  if (input.switchingWorkspace) return { action: 'hold', reason: 'switching-workspace' };
  if (input.streaming) return { action: 'hold', reason: 'streaming' };
  if (input.uploading) return { action: 'hold', reason: 'uploading' };
  return { action: 'ask' };
}

/** REACT_APP_TERMS_UPDATE_PROMPT_ACCOUNTS: a comma list of account ids, `*` for everyone. Empty or absent: off. */
export function termsPromptLeverOn(uid: string | null | undefined, csv: string | undefined): boolean {
  const id = String(uid ?? '').trim();
  if (!id) return false;
  const listed = String(csv ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  return listed.includes('*') || listed.includes(id);
}
